import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasPageAccess, hasFeatureAccess } from '../utils/accessControl';
import { getAllPageNames } from '../config/permissionsRegistry';

/**
 * ProtectedRoute component that checks if the user is authenticated and has page access
 * @param {Object} props - Component props
 * @param {string} props.pageName - Page name for permission checking
 * @param {string} props.requiredFeature - Optional feature name to check (e.g., 'sendBroadcasts')
 * @param {React.ReactNode} props.children - The children to render if the user has access
 * @returns {React.ReactNode} - The children or a redirect
 */
// Tracks which pageNames have already been warned about so we only log once per session.
const _warnedPageNames = new Set();

const ProtectedRoute = ({ pageName, requiredFeature, children }) => {
  const { currentUser, userRole, userLandingPage, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navigationTimeoutRef = useRef(null);
  const [checkingPermissions, setCheckingPermissions] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  // ── Dev-mode registry check ───────────────────────────────────────────────
  // Fires once per unique pageName to catch pages added to routes but forgotten
  // in permissionsRegistry.js. Does nothing in production.
  if (process.env.NODE_ENV === 'development' && pageName && !_warnedPageNames.has(pageName)) {
    if (!getAllPageNames().includes(pageName)) {
      _warnedPageNames.add(pageName);
      console.error(
        `%c[ProtectedRoute] RBAC WARNING`,
        'color: #ff4444; font-weight: bold;',
        `\npageName "${pageName}" is used in a route but is NOT in permissionsRegistry.js!\n` +
        `→ Add it to APP_PAGES in src/config/permissionsRegistry.js\n` +
        `→ Then click "Auto-Discover" in Settings → Access Levels`
      );
    }
  }
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    // Clear any existing navigation timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }

    const checkAccess = async () => {
      // Wait for auth to finish loading
      if (loading) {
        return;
      }

      // Check authentication first
      if (!currentUser) {
      if (location.pathname !== '/login') {
        navigationTimeoutRef.current = setTimeout(() => {
          navigate('/login');
        }, 100);
      }
        return;
      }

      // Block restricted public roles from accessing main app
      // These roles are for anonymous users (contractors/tenants) who should only access public portals
      const restrictedRoles = ['public_contractor', 'public_tenant', 'public_anonymous'];
      if (userRole && restrictedRoles.includes(userRole)) {
        console.warn(`User with restricted role '${userRole}' attempted to access main app - redirecting`);
        // Redirect to appropriate public portal based on role
        if (userRole === 'public_contractor') {
          navigationTimeoutRef.current = setTimeout(() => {
            navigate('/contractor');
          }, 100);
        } else if (userRole === 'public_tenant') {
          navigationTimeoutRef.current = setTimeout(() => {
            navigate('/tenant');
          }, 100);
        } else {
          // For public_anonymous or unknown restricted role, redirect to login
          navigationTimeoutRef.current = setTimeout(() => {
            navigate('/login');
          }, 100);
        }
        setHasAccess(false);
        setCheckingPermissions(false);
        return;
      }

      // If no pageName specified, just check authentication
      if (!pageName) {
        setHasAccess(true);
        setCheckingPermissions(false);
        return;
      }

      // Check page access permissions
      try {
        const hasPagePermission = await hasPageAccess(currentUser.uid, userRole, pageName);
        
        // Also check feature permission if requiredFeature is specified
        let hasFeaturePermission = true;
        if (requiredFeature) {
          hasFeaturePermission = await hasFeatureAccess(currentUser.uid, userRole, requiredFeature);
        }
        
        const hasAccess = hasPagePermission && hasFeaturePermission;
        setHasAccess(hasAccess);
        
        if (!hasAccess && location.pathname !== (userLandingPage || '/dashboard')) {
          if (!hasPagePermission) {
            console.warn(`User ${currentUser.email} denied access to page: ${pageName}`);
          }
          if (requiredFeature && !hasFeaturePermission) {
            console.warn(`User ${currentUser.email} denied access to feature: ${requiredFeature}`);
          }
          const redirectTo = userLandingPage || '/dashboard';
          navigationTimeoutRef.current = setTimeout(() => {
            navigate(redirectTo);
          }, 100);
        }
      } catch (error) {
        console.error('Error checking page access:', error);
        setHasAccess(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkAccess();

    // Cleanup function
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [currentUser, userRole, userLandingPage, loading, pageName, requiredFeature, navigate, location]);

  // Show loading while checking auth or permissions
  if (loading || checkingPermissions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  // If not authenticated or no access, redirect will happen in useEffect
  if (!currentUser || !hasAccess) {
    return null;
  }

  // User is authenticated and has access, render children
  return children;
};

export default ProtectedRoute; 
